/**
 * v2.56 — PriceJobs page.
 *
 * Lists all price upload jobs for this session. Each row links to the detail
 * page at /price-uploads/:id.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Text,
  Title,
} from '@tremor/react';
import { listPriceJobs } from '../api/priceUpload';
import type { PriceJob } from '../api/types';

// Badge color per job state
function stateColor(state: string): 'gray' | 'yellow' | 'blue' | 'green' | 'red' {
  switch (state) {
    case 'done':     return 'green';
    case 'failed':   return 'red';
    case 'running':  return 'blue';
    case 'deferring':return 'yellow';
    case 'queued':   return 'gray';
    default:         return 'gray';
  }
}

export default function PriceJobs() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<PriceJob[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    listPriceJobs()
      .then((data) => { if (mounted.current) setJobs(data); })
      .catch((e: Error) => { if (mounted.current) setErr(e.message ?? String(e)); });
    return () => { mounted.current = false; };
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1 px-6 py-6 space-y-4 max-w-screen-xl mx-auto w-full">
        <div>
          <Title>Cargas de precios</Title>
          <Text className="text-tremor-content text-sm">
            Historial de jobs de actualización de precios
          </Text>
        </div>

        {err && (
          <Text className="text-red-600 dark:text-red-400 text-sm">{err}</Text>
        )}

        <Card>
          {jobs.length === 0 && !err ? (
            <Text className="mt-3 text-tremor-content text-sm italic">Sin cargas registradas</Text>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>ID</TableHeaderCell>
                    <TableHeaderCell>Creado por</TableHeaderCell>
                    <TableHeaderCell>Fecha</TableHeaderCell>
                    <TableHeaderCell>Estado</TableHeaderCell>
                    <TableHeaderCell className="text-right">OK</TableHeaderCell>
                    <TableHeaderCell className="text-right">Creados</TableHeaderCell>
                    <TableHeaderCell className="text-right">Diferidos</TableHeaderCell>
                    <TableHeaderCell className="text-right">Errores</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobs.map((job) => (
                    <TableRow
                      key={job.id}
                      className="cursor-pointer hover:bg-tremor-background-muted"
                      onClick={() => navigate(`/price-uploads/${job.id}`)}
                    >
                      <TableCell className="font-mono text-sm">{job.id}</TableCell>
                      <TableCell>{job.created_by}</TableCell>
                      <TableCell className="tabular-nums text-sm">
                        {new Date(job.created_at).toLocaleString('es-MX')}
                      </TableCell>
                      <TableCell>
                        <Badge color={stateColor(job.state)}>{job.state}</Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{job.ok}</TableCell>
                      <TableCell className="text-right tabular-nums">{job.created}</TableCell>
                      <TableCell className="text-right tabular-nums">{job.deferred}</TableCell>
                      <TableCell className="text-right tabular-nums">{job.errored}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      </main>
    </div>
  );
}
